// TOUR-3 — pure tour runner state machine (B3.6 session controller core).
// Non-destructive by default; cancel always available while running.

import type { FireflyIntent } from "@/components/activity/firefly/fireflyIntents";
import type {
  FireflyTourArtifact,
  FireflyTourRunnerEvent,
  FireflyTourRunnerState,
  FireflyTourRunnerStatus,
  FireflyTourStep,
  FireflyTourStepRuntime,
  FireflyTourTrigger,
} from "./tourTypes";
import { FIREFLY_TOUR_DEFAULT_DWELL_MS } from "./tourTypes";

export function createIdleFireflyTourRunnerState(): FireflyTourRunnerState {
  return {
    status: "idle",
    artifact: null,
    steps: [],
    currentIndex: 0,
    trigger: null,
    intent: null,
    currentStep: null,
    canCancel: false,
    canAdvance: false,
  };
}

export function resolveTourSessionIntent(
  trigger: FireflyTourTrigger,
  artifact: FireflyTourArtifact,
): Extract<FireflyIntent, "teaching" | "discovery" | "guidance"> {
  if (trigger === "discovery") return "discovery";
  return artifact.defaultIntent;
}

export function deriveFireflyTourStepRuntime(
  step: FireflyTourStep,
  index: number,
): FireflyTourStepRuntime {
  const describeOnly = step.destructive === true;
  const waitForUserAction = !describeOnly && step.waitForClick === true;
  const advanceOn: FireflyTourStepRuntime["advanceOn"] = describeOnly
    ? "next_button"
    : waitForUserAction
      ? "next_button"
      : step.advanceOn ?? "next_button";
  const dwellMs = step.dwellMs ?? FIREFLY_TOUR_DEFAULT_DWELL_MS;

  return {
    step,
    index,
    waitForUserAction,
    describeOnly,
    advanceOn: waitForUserAction ? "next_button" : advanceOn,
    dwellMs,
  };
}

function buildRunningState(
  artifact: FireflyTourArtifact,
  trigger: FireflyTourTrigger,
  index: number,
): FireflyTourRunnerState {
  const steps = artifact.steps;
  const step = steps[index];
  const currentStep = step ? deriveFireflyTourStepRuntime(step, index) : null;

  return {
    status: "running",
    artifact,
    steps,
    currentIndex: index,
    trigger,
    intent: resolveTourSessionIntent(trigger, artifact),
    currentStep,
    canCancel: true,
    canAdvance: currentStep ? canAdvanceFromStep(currentStep) : false,
  };
}

function canAdvanceFromStep(runtime: FireflyTourStepRuntime): boolean {
  if (runtime.waitForUserAction) return false;
  return runtime.advanceOn === "next_button" || runtime.advanceOn === "timer";
}

function terminalState(
  prev: FireflyTourRunnerState,
  status: Extract<FireflyTourRunnerStatus, "completed" | "cancelled">,
): FireflyTourRunnerState {
  return {
    ...prev,
    status,
    currentStep: null,
    canCancel: false,
    canAdvance: false,
  };
}

function advanceIndex(state: FireflyTourRunnerState): FireflyTourRunnerState {
  if (!state.artifact || !state.trigger) return state;
  const nextIndex = state.currentIndex + 1;
  if (nextIndex >= state.steps.length) {
    return terminalState(state, "completed");
  }
  return buildRunningState(state.artifact, state.trigger, nextIndex);
}

function canAcceptAdvance(state: FireflyTourRunnerState, via: "next" | "user_action" | "timer"): boolean {
  const runtime = state.currentStep;
  if (state.status !== "running" || !runtime) return false;

  if (via === "user_action") {
    return runtime.waitForUserAction;
  }
  if (via === "timer") {
    return !runtime.waitForUserAction && runtime.advanceOn === "timer";
  }
  return !runtime.waitForUserAction && runtime.advanceOn === "next_button";
}

export function reduceFireflyTourRunner(
  state: FireflyTourRunnerState,
  event: FireflyTourRunnerEvent,
): FireflyTourRunnerState {
  switch (event.type) {
    case "start": {
      if (!event.artifact.steps.length) {
        return createIdleFireflyTourRunnerState();
      }
      return buildRunningState(event.artifact, event.trigger, 0);
    }
    case "cancel": {
      if (state.status !== "running") return state;
      return terminalState(state, "cancelled");
    }
    case "reset":
      return createIdleFireflyTourRunnerState();
    case "next": {
      if (!canAcceptAdvance(state, "next")) return state;
      return advanceIndex(state);
    }
    case "user_action": {
      if (!canAcceptAdvance(state, "user_action")) return state;
      return advanceIndex(state);
    }
    case "timer_elapsed": {
      if (!canAcceptAdvance(state, "timer")) return state;
      return advanceIndex(state);
    }
    default:
      return state;
  }
}

export function getFireflyTourRunnerStepCount(state: FireflyTourRunnerState): number {
  return state.steps.length;
}

/** Test helper — replay a scripted interaction against mock steps. */
export function replayFireflyTourRunner(
  artifact: FireflyTourArtifact,
  trigger: FireflyTourTrigger,
  script: Array<"next" | "user_action" | "timer" | "cancel">,
): FireflyTourRunnerState {
  let state = reduceFireflyTourRunner(createIdleFireflyTourRunnerState(), {
    type: "start",
    artifact,
    trigger,
  });

  for (const action of script) {
    if (state.status !== "running") break;
    state = reduceFireflyTourRunner(state, { type: action === "timer" ? "timer_elapsed" : action });
  }

  return state;
}
